import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CreditCard, Lock, CheckCircle, X, FileText, Tag, Wallet, Coins, Loader2, ShieldCheck, Sparkles, ChevronRight, CircleCheck, PlayCircle } from 'lucide-react';
import { cardTheme } from '../styles/cardTheme';
import { useAuth } from '../contexts/AuthContext';
import { Course } from '../types/course';
import { StandaloneUnitDto } from '../types/unit';
import { promoCodeService, orderService } from '../services/api';
import { walletService } from '../services/walletService';
import { formatCoins } from '../utils/walletLabels';
import { ValidatePromoCodeApiResponse, CreateOrderWithPaymentApiResponse } from '../types/payment';
import InsufficientWalletModal from '../components/checkout/InsufficientWalletModal';
import PaymentSuccessModal from '../components/checkout/PaymentSuccessModal';
import {
  PaymentSuccessDetails,
  savePendingCoursePayment,
} from '../types/paymentSuccess';

const CheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, isLoading } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [unit, setUnit] = useState<StandaloneUnitDto | null>(null);
  const [loading, setLoading] = useState(true);
  const hasRedirectedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'wallet' | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [isLoadingWallet, setIsLoadingWallet] = useState(false);
  const [showInsufficientWalletModal, setShowInsufficientWalletModal] = useState(false);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentSuccessDetails, setPaymentSuccessDetails] = useState<PaymentSuccessDetails | null>(null);
  const [showPaymentVideo, setShowPaymentVideo] = useState(false);
  
  // Promo code states
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoCodeValidation, setPromoCodeValidation] = useState<ValidatePromoCodeApiResponse['data'] | null>(null);
  const [isValidatingPromoCode, setIsValidatingPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);

  // Auto-select online payment for paid items
  useEffect(() => {
    const price = course?.price ?? unit?.price ?? 0;
    if (price !== 0 && !paymentMethod) {
      setPaymentMethod('online');
    }
  }, [course, unit, paymentMethod]);

  useEffect(() => {
    const loadWalletBalance = async () => {
      if (!isAuthenticated) return;
      setIsLoadingWallet(true);
      const result = await walletService.getMyHistory(1, 1);
      if (result.success) {
        setWalletBalance(result.data.history.currentBalance);
      }
      setIsLoadingWallet(false);
    };
    loadWalletBalance();
  }, [isAuthenticated]);

  // Get course or unit from location state
  useEffect(() => {
    const fetchCheckoutItem = async () => {
      try {
        setLoading(true);
        setError(null);

        if (location.state?.unit) {
          setUnit(location.state.unit);
          setCourse(null);
          setLoading(false);
          return;
        }

        if (location.state?.course) {
          setCourse(location.state.course);
          setUnit(null);
          setLoading(false);
          return;
        }

        if (location.state?.courseId) {
          setError('Course information not found');
        } else if (location.state?.unitId) {
          setError('معلومات الحصة غير متوفرة');
        } else {
          setError('No course or unit information provided');
        }
      } catch (err) {
        console.error('Error loading checkout item:', err);
        setError('Failed to load checkout information');
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutItem();
  }, [location.state]);

  // Redirect to login if not authenticated
  useEffect(() => {
    // Only redirect once, and only if NOT loading and NOT authenticated
    if (!isLoading && !isAuthenticated && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      // Get course from current state or location state for redirect
      const courseData = course || location.state?.course;
      const unitData = unit || location.state?.unit;
      navigate('/login', { 
        state: { 
          redirectTo: '/checkout',
          course: courseData,
          courseId: courseData?.id,
          unit: unitData,
          unitId: unitData?.id,
        } 
      });
    }
  }, [isAuthenticated, isLoading, navigate, course, unit, location.state]);

  const formatPrice = (price: number): string => {
    if (price === 0) return '0';
    return price.toLocaleString('ar-EG');
  };

  const handleValidatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError('يرجى إدخال كود الخصم');
      return;
    }

    if (!course) {
      setPromoCodeError('معلومات الكورس غير متوفرة');
      return;
    }

    setIsValidatingPromoCode(true);
    setPromoCodeError(null);
    setPromoCodeValidation(null);

    try {
      const response: ValidatePromoCodeApiResponse = await promoCodeService.validatePromoCode(
        promoCode.trim(),
        course.id
      );

      if (response.success && response.data?.isValid) {
        setPromoCodeValidation(response.data);
        setPromoCodeError(null);
      } else {
        setPromoCodeValidation(null);
        setPromoCodeError(
          response.data?.errorMessage || 
          response.message || 
          'كود الخصم غير صحيح أو غير صالح'
        );
      }
    } catch (err: any) {
      console.error('Promo code validation error:', err);
      setPromoCodeValidation(null);
      setPromoCodeError('حدث خطأ أثناء التحقق من كود الخصم');
    } finally {
      setIsValidatingPromoCode(false);
    }
  };

  // Remove promo code
  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoCodeValidation(null);
    setPromoCodeError(null);
  };

  const handlePayment = async () => {
    const itemPrice = course ? (course.price ?? 0) : (unit?.price ?? 0);
    const itemName = course
      ? course.courseName
      : unit
      ? unit.titel || unit.unitTitle || unit.unitName
      : '';

    if (!course && !unit) {
      setError('معلومات الطلب غير متوفرة');
      return;
    }

    if (!isAuthenticated || !currentUser) {
      navigate('/login', { 
        state: { 
          redirectTo: '/checkout',
          course: course ?? undefined,
          courseId: course?.id,
          unit: unit ?? undefined,
          unitId: unit?.id,
        } 
      });
      return;
    }

    if (itemPrice === 0) {
      if (course) {
        navigate(`/course/${course.id}/curriculum`);
      } else if (unit) {
        const result = await walletService.buyUnitWithCoins(unit.id);
        if (result.success) {
          navigate(`/unit-viewer/${unit.id}`);
        } else {
          setError(result.error);
        }
      }
      return;
    }

    if (!paymentMethod) {
      setError('يرجى اختيار طريقة الدفع');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      if (paymentMethod === 'online') {
        const response: CreateOrderWithPaymentApiResponse = await orderService.createOrderWithPayment({
          courseId: course?.id,
          unitId: unit?.id,
          promoCode: course && promoCodeValidation ? promoCode.trim() : undefined,
          customerEmail: currentUser.email,
        });

        if (response.success && response.data?.paymentSession) {
          const sessionUrl = response.data.paymentSession.sessionUrl;
          const order = response.data.order;

          if (sessionUrl) {
            const paidOriginal = promoCodeValidation?.originalPrice ?? itemPrice;
            const paidDiscount = promoCodeValidation?.discountAmount ?? 0;
            const paidNet = promoCodeValidation?.netPrice ?? paidOriginal;
            savePendingCoursePayment({
              courseId: course?.id ?? String(unit?.id ?? ''),
              courseName: itemName,
              amount: order?.netPrice ?? paidNet,
              currency: response.data.paymentSession.currency ?? 'EGP',
              paymentMethod: 'online',
              orderId: order?.id,
              promoCode: order?.promoCode ?? (promoCodeValidation ? promoCode.trim() : undefined),
              discountAmount: order?.discountAmount ?? paidDiscount,
              originalPrice: order?.originalPrice ?? paidOriginal,
            });
            window.location.href = sessionUrl;
          } else {
            setError('لم يتم العثور على رابط بوابة الدفع. الرجاء المحاولة مرة أخرى.');
            setIsProcessing(false);
          }
        } else {
          setError(response.message || 'حدث خطأ أثناء إنشاء الطلب. الرجاء المحاولة مرة أخرى.');
          setIsProcessing(false);
        }
      } else if (paymentMethod === 'wallet') {
        const paidOriginal = promoCodeValidation?.originalPrice ?? itemPrice;
        const paidNet = promoCodeValidation?.netPrice ?? paidOriginal;
        const walletCoins = Math.max(0, Math.round(paidNet));

        if (walletCoins > 0 && (walletBalance === null || walletBalance < walletCoins)) {
          setIsProcessing(false);
          setShowInsufficientWalletModal(true);
          return;
        }

        const result = course
          ? await walletService.buyCourseWithCoins(
              course.id,
              promoCodeValidation ? promoCode.trim() : undefined
            )
          : await walletService.buyUnitWithCoins(unit!.id);

        if (result.success) {
          const paidDiscount = promoCodeValidation?.discountAmount ?? 0;
          setPaymentSuccessDetails({
            courseId: course?.id ?? String(unit?.id ?? ''),
            courseName: result.data.courseName ?? result.data.unitName ?? itemName,
            amount: walletCoins,
            currency: 'EGP',
            paymentMethod: 'wallet',
            transactionId: result.data.transactionId,
            promoCode: promoCodeValidation ? promoCode.trim() : undefined,
            discountAmount: paidDiscount,
            originalPrice: paidOriginal,
            coinsSpent: result.data.coinsSpent,
            balanceAfter: result.data.newBalance,
            paidAt: new Date().toISOString(),
          });
          setShowPaymentSuccessModal(true);
          setIsProcessing(false);
        } else {
          setError(result.error);
          setIsProcessing(false);
        }
      }
    } catch (err: any) {
      console.error('Payment error:', err?.response?.status, err?.response?.data ?? err);
      setError('حدث خطأ أثناء معالجة الدفع. الرجاء المحاولة مرة أخرى.');
      setIsProcessing(false);
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-green-950 px-4" dir="rtl">
        <div className="text-center">
          <div className="relative mx-auto mb-6 h-16 w-16">
            <div className="absolute inset-0 rounded-full border-4 border-gold-500/20" />
            <Loader2 className="absolute inset-0 m-auto h-10 w-10 animate-spin text-gold-400" />
          </div>
          <p className="text-sm font-medium text-green-100/90 sm:text-base">
            {isLoading ? 'جاري التحقق من الحساب...' : 'جاري تحميل الطلب...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  if (error && !course && !unit) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-green-950 via-green-900 to-green-950 px-4" dir="rtl">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/95 p-8 text-center shadow-2xl backdrop-blur">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50">
            <X className="h-8 w-8 text-red-500" />
          </div>
          <h2 className={`mb-2 text-xl font-bold ${cardTheme.title}`}>تعذّر إتمام الطلب</h2>
          <p className="mb-6 text-sm text-gray-600">{error}</p>
          <Link
            to="/student-portal"
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-2xl px-6 ${cardTheme.btnPrimary}`}
          >
            العودة للبوابة
          </Link>
        </div>
      </div>
    );
  }

  if (!course && !unit) return null;

  const checkoutPrice = course ? (course.price ?? 0) : (unit?.price ?? 0);
  const checkoutTitle = course
    ? course.courseName
    : unit
    ? unit.titel || unit.unitTitle || unit.unitName
    : '';
  const isUnitCheckout = Boolean(unit && !course);
  const itemLabel = isUnitCheckout ? 'حصة' : 'كورس';

  const isFree = checkoutPrice === 0;
  const originalPrice = promoCodeValidation?.originalPrice ?? checkoutPrice;
  const discountAmount = promoCodeValidation?.discountAmount ?? 0;
  const netPrice = promoCodeValidation?.netPrice ?? originalPrice;
  const coinsRequired = Math.max(0, Math.round(netPrice));
  const hasEnoughCoins = walletBalance !== null && walletBalance >= coinsRequired;

  const totalDisplay = isFree
    ? 'مجاناً'
    : paymentMethod === 'wallet'
    ? coinsRequired > 0
      ? `${formatCoins(coinsRequired)} عملة`
      : 'مجاناً'
    : `${formatPrice(netPrice)} جنيه`;

  const canPay = !isProcessing && (isFree || !!paymentMethod);

  const payButtonLabel = isProcessing
    ? 'جاري المعالجة...'
    : isFree
    ? 'تسجيل مجاني'
    : paymentMethod === 'wallet'
    ? `ادفع ${formatCoins(coinsRequired)} من المحفظة`
    : `ادفع ${formatPrice(netPrice)} جنيه`;

  const steps = [
    { id: 1, label: 'المحتوى', done: true },
    { id: 2, label: 'الدفع', done: isFree || !!paymentMethod },
    { id: 3, label: 'التأكيد', done: false },
  ];

  const paymentGuideVideoUrl = 'https://www.youtube.com/embed/7_DITBdYz7Q?si=lG30i9_7YqMnkYRT';

  const priceBreakdown = (
    <div className="space-y-3 rounded-2xl border border-green-100 bg-green-50/40 p-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">سعر {itemLabel}</span>
        <span className="font-semibold text-gray-900">
          {isFree ? 'مجاناً' : `${formatPrice(originalPrice)} جنيه`}
        </span>
      </div>
      {promoCodeValidation && discountAmount > 0 && (
        <div className="flex items-center justify-between text-sm text-green-700">
          <span>خصم الكود</span>
          <span className="font-semibold">-{formatPrice(discountAmount)} جنيه</span>
        </div>
      )}
      {paymentMethod === 'wallet' && !isFree && coinsRequired > 0 && (
        <div className="flex items-center justify-between text-sm text-gold-800">
          <span className="flex items-center gap-1">
            <Coins className="h-4 w-4" />
            خصم من المحفظة
          </span>
          <span className="font-semibold">{formatCoins(coinsRequired)}</span>
        </div>
      )}
      <div className="border-t border-green-200/80 pt-3 flex items-center justify-between">
        <span className="font-bold text-green-900">الإجمالي</span>
        <span className={`text-xl font-bold ${cardTheme.price}`}>{totalDisplay}</span>
      </div>
    </div>
  );

  const promoSection =
    !isFree && !isUnitCheckout ? (
      <div className="rounded-2xl border border-dashed border-gold-300/80 bg-gold-50/30 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Tag className="h-4 w-4 text-gold-600" />
          <span className="text-sm font-bold text-green-900">عندك كود خصم؟</span>
        </div>
        {!promoCodeValidation ? (
          <div className="space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setPromoCodeError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isValidatingPromoCode) handleValidatePromoCode();
                }}
                placeholder="XXXXXX"
                className="min-h-11 flex-1 rounded-xl border border-green-200 bg-white px-4 text-sm tracking-widest uppercase focus:border-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-400/25"
                disabled={isValidatingPromoCode || isProcessing}
              />
              <button
                type="button"
                onClick={handleValidatePromoCode}
                disabled={isValidatingPromoCode || isProcessing || !promoCode.trim()}
                className={`min-h-11 rounded-xl px-5 text-sm font-bold disabled:opacity-50 ${cardTheme.btnGold}`}
              >
                {isValidatingPromoCode ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'تطبيق'}
              </button>
            </div>
            {promoCodeError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{promoCodeError}</p>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2 min-w-0">
              <CircleCheck className="h-5 w-5 shrink-0 text-green-600" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-green-800">{promoCode.toUpperCase()}</p>
                {promoCodeValidation.promoCodeDetails && (
                  <p className="truncate text-xs text-green-600">{promoCodeValidation.promoCodeDetails}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemovePromoCode}
              disabled={isProcessing}
              className="shrink-0 text-xs font-bold text-red-600 hover:text-red-700"
            >
              إزالة
            </button>
          </div>
        )}
      </div>
    ) : null;

  const paymentSection = !isFree ? (
    <div className="space-y-3">
      <h3 className="text-sm font-bold text-green-900">اختر طريقة الدفع</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setPaymentMethod('online')}
          className={`group relative overflow-hidden rounded-2xl border-2 p-4 text-right transition-all min-h-[120px] ${
            paymentMethod === 'online'
              ? 'border-gold-400 bg-white shadow-lg shadow-green-900/10'
              : 'border-green-100 bg-white hover:border-green-300'
          }`}
        >
          {paymentMethod === 'online' && (
            <span className="absolute top-3 left-3 rounded-full bg-green-700 p-0.5">
              <CheckCircle className="h-4 w-4 text-white" />
            </span>
          )}
          <div className={`mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl ${cardTheme.iconBox}`}>
            <CreditCard className="h-6 w-6" />
          </div>
          <p className="font-bold text-green-900">دفع إلكتروني</p>
          <p className="mt-1 text-xs text-gray-500 leading-relaxed">فيزا · ماستركارد · محافظ رقمية</p>
        </button>

        <button
          type="button"
          onClick={() => setPaymentMethod('wallet')}
          className={`group relative overflow-hidden rounded-2xl border-2 p-4 text-right transition-all min-h-[120px] ${
            paymentMethod === 'wallet'
              ? 'border-gold-400 bg-white shadow-lg shadow-green-900/10'
              : 'border-green-100 bg-white hover:border-green-300'
          }`}
        >
          {paymentMethod === 'wallet' && (
            <span className="absolute top-3 left-3 rounded-full bg-green-700 p-0.5">
              <CheckCircle className="h-4 w-4 text-white" />
            </span>
          )}
          <div className={`mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl ${cardTheme.iconBoxGold}`}>
            <Wallet className="h-6 w-6" />
          </div>
          <p className="font-bold text-green-900">محفظتي</p>
          <p className="mt-1 text-xs text-gray-500 leading-relaxed">
            {isLoadingWallet
              ? 'جاري التحميل...'
              : walletBalance !== null
              ? `الرصيد: ${formatCoins(walletBalance)}`
              : 'الدفع بالعملات'}
          </p>
          {!isLoadingWallet && walletBalance !== null && !hasEnoughCoins && (
            <Link
              to="/student-portal/wallet"
              onClick={(e) => e.stopPropagation()}
              className="mt-2 inline-block text-xs font-bold text-green-700 underline"
            >
              شحن المحفظة
            </Link>
          )}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-paper font-cairo" dir="rtl">
      {/* Top nav */}
      <header className="sticky top-0 z-40 border-b border-green-100/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/student-portal"
            className="inline-flex min-h-10 items-center gap-1.5 text-sm font-semibold text-green-800 hover:text-green-600"
          >
            <ChevronRight className="h-5 w-5" />
            رجوع
          </Link>
          <span className="text-sm font-bold text-green-900">إتمام الشراء</span>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <ShieldCheck className="h-4 w-4 text-gold-500" />
            آمن
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8 pb-28 lg:pb-10">
        {/* Item summary — text only */}
        <div className={`${cardTheme.shell} mb-6 p-5 sm:p-6`}>
          <span className={`inline-flex items-center gap-2 mb-3 ${cardTheme.badgeGold}`}>
            <Sparkles className="h-3.5 w-3.5" />
            {isUnitCheckout ? 'حصة مستقلة' : 'كورس تعليمي'}
          </span>
          <h1 className={`text-xl font-bold leading-snug sm:text-2xl ${cardTheme.title}`}>
            {checkoutTitle}
          </h1>
          {course?.description && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">{course.description}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {course?.grade && <span className={cardTheme.badge}>{course.grade}</span>}
            {course?.term && (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600">
                {course.term}
              </span>
            )}
            {isFree && (
              <span className="rounded-full bg-gold-500 px-3 py-1 text-xs font-bold text-green-950">مجاني</span>
            )}
            <span className={`text-base font-bold ${cardTheme.price}`}>
              {isFree ? 'مجاناً' : `${formatPrice(checkoutPrice)} جنيه`}
            </span>
          </div>
        </div>

        {/* Steps */}
        <div className="mb-8 flex items-center justify-between gap-2">
          {steps.map((step, i) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-colors ${
                    step.done
                      ? 'bg-green-700 text-white shadow-md shadow-green-900/20'
                      : i === 1 && !isFree
                      ? 'border-2 border-gold-400 bg-white text-gold-700'
                      : 'border border-gray-200 bg-white text-gray-400'
                  }`}
                >
                  {step.done ? <CheckCircle className="h-5 w-5" /> : step.id}
                </div>
                <span
                  className={`text-[10px] sm:text-xs font-semibold truncate ${
                    step.done ? 'text-green-800' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`h-0.5 flex-1 rounded ${step.done ? 'bg-green-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="space-y-5">
          {paymentSection}
          <div className={`${cardTheme.shell} p-4 sm:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h3 className={`text-sm font-bold sm:text-base ${cardTheme.title}`}>
                  فيديو توضيحي لطريقة الدفع على المنصة
                </h3>
                <p className={`mt-1 text-xs sm:text-sm ${cardTheme.subtitle}`}>
                  لو حابب تشوف خطوات الدفع قبل الإكمال، افتح الفيديو التوضيحي.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPaymentVideo(true)}
                className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold sm:w-auto ${cardTheme.btnOutline}`}
              >
                <PlayCircle className="h-5 w-5" />
                مشاهدة الفيديو
              </button>
            </div>
          </div>
          {promoSection}
          {priceBreakdown}

          {error && (
            <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-4">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Desktop CTA */}
          <div className="hidden lg:block space-y-4 pt-2">
            <button
              type="button"
              onClick={handlePayment}
              disabled={!canPay}
              className={`flex w-full min-h-14 items-center justify-center gap-2 rounded-2xl text-base font-bold text-white transition-all disabled:cursor-not-allowed disabled:opacity-50 ${cardTheme.btnPrimary} ${
                canPay ? 'shadow-xl shadow-green-900/25 hover:shadow-2xl hover:-translate-y-0.5' : ''
              }`}
            >
              {isProcessing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Lock className="h-5 w-5" />
              )}
              {payButtonLabel}
            </button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
              <ShieldCheck className="h-3.5 w-3.5" />
              SSL · بياناتك محمية بالكامل
            </p>
            <Link
              to="/refund-policy"
              className="flex items-center justify-center gap-1.5 text-sm font-medium text-green-700 hover:underline"
            >
              <FileText className="h-4 w-4" />
              سياسة الاسترجاع
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile sticky pay bar */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-green-100 bg-white/95 p-4 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur-lg lg:hidden">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm text-gray-600">الإجمالي</span>
          <span className={`text-lg font-bold ${cardTheme.price}`}>{totalDisplay}</span>
        </div>
        <button
          type="button"
          onClick={handlePayment}
          disabled={!canPay}
          className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-2xl font-bold text-white disabled:opacity-50 ${cardTheme.btnPrimary}`}
        >
          {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
          {payButtonLabel}
        </button>
      </div>

      <InsufficientWalletModal
        isOpen={showInsufficientWalletModal}
        onClose={() => setShowInsufficientWalletModal(false)}
        currentBalance={walletBalance ?? 0}
        coinsRequired={coinsRequired}
      />

      <PaymentSuccessModal
        isOpen={showPaymentSuccessModal}
        onClose={() => {
          setShowPaymentSuccessModal(false);
          if (paymentSuccessDetails?.courseId) {
            if (unit && !course) {
              navigate(`/unit-viewer/${unit.id}`);
            } else {
              navigate(`/course-player/${paymentSuccessDetails.courseId}`);
            }
          }
        }}
        details={paymentSuccessDetails}
      />

      {showPaymentVideo && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-green-950/75 px-3 py-6 backdrop-blur-sm sm:px-6"
          dir="rtl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-video-title"
          onClick={() => setShowPaymentVideo(false)}
        >
          <div
            className={`${cardTheme.shell} w-full max-w-4xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-green-100 px-4 py-3 sm:px-5">
              <h2 id="payment-video-title" className={`text-sm font-bold sm:text-base ${cardTheme.title}`}>
                فيديو توضيحي لطريقة الدفع على المنصة
              </h2>
              <button
                type="button"
                onClick={() => setShowPaymentVideo(false)}
                className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-xl text-green-800 hover:bg-green-50"
                aria-label="إغلاق الفيديو"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-3 sm:p-5">
              <div className="overflow-hidden rounded-2xl border border-green-100 bg-black shadow-sm">
                <div className="relative w-full pt-[56.25%]">
                  <iframe
                    className="absolute inset-0 h-full w-full"
                    src={paymentGuideVideoUrl}
                    title="فيديو توضيحي لطريقة الدفع على المنصة"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
